export type WebSerialPort = {
  readonly readable: ReadableStream<Uint8Array> | null
  readonly writable: WritableStream<Uint8Array> | null
  open(options: {
    baudRate: number
    dataBits?: 7 | 8
    stopBits?: 1 | 2
    parity?: 'none' | 'even' | 'odd'
    bufferSize?: number
    flowControl?: 'none' | 'hardware'
  }): Promise<void>
  close(): Promise<void>
  setSignals?(signals: {
    dataTerminalReady?: boolean
    requestToSend?: boolean
    break?: boolean
  }): Promise<void>
}

export type SerialSignalState = {
  dataTerminalReady?: boolean
  requestToSend?: boolean
}

type SerialNavigator = Navigator & {
  serial?: {
    requestPort(options?: {
      filters?: Array<{ usbVendorId?: number; usbProductId?: number }>
    }): Promise<WebSerialPort>
    getPorts(): Promise<WebSerialPort[]>
  }
}

export function hasWebSerial(): boolean {
  return typeof navigator !== 'undefined' && Boolean((navigator as SerialNavigator).serial)
}

export async function requestWebSerialPort(): Promise<WebSerialPort> {
  const serial = (navigator as SerialNavigator).serial
  if (!serial) {
    throw new Error(
      'Web Serial is not available in this browser. Use desktop Chrome or Edge over HTTPS.',
    )
  }
  return serial.requestPort()
}

export interface RadioTransport {
  discardInput(): Promise<void>
  write(data: Uint8Array): Promise<void>
  readExact(size: number, timeoutMs?: number): Promise<Uint8Array>
  readUntil?(delimiter: number, timeoutMs?: number): Promise<Uint8Array>
  reconfigure?(options: {
    baudRate?: number
    stopBits?: 1 | 2
  }): Promise<void>
  setSignals?(signals: {
    dataTerminalReady?: boolean
    requestToSend?: boolean
  }): Promise<void>
}

const READ_SLICE_MS = 100

export class WebSerialRadioTransport implements RadioTransport {
  private readonly port: WebSerialPort
  private baudRate: number
  private parity: 'none' | 'even' | 'odd'
  private stopBits: 1 | 2
  private reader: ReadableStreamDefaultReader<Uint8Array> | null = null
  private readLoop: Promise<void> | null = null
  private buffer = new Uint8Array(0)
  private waiters = new Set<() => void>()
  private failure: Error | null = null
  private opened = false

  constructor(
    port: WebSerialPort,
    options: {
      baudRate?: number
      parity?: 'none' | 'even' | 'odd'
      stopBits?: 1 | 2
    } = {},
  ) {
    this.port = port
    this.baudRate = options.baudRate ?? 115200
    this.parity = options.parity ?? 'none'
    this.stopBits = options.stopBits ?? 1
  }

  async open(options: {
    signals?: SerialSignalState
    pulseSignals?: boolean
  } = {}): Promise<void> {
    if (this.opened) return
    try {
      await this.port.open({
        baudRate: this.baudRate,
        dataBits: 8,
        stopBits: this.stopBits,
        parity: this.parity,
        flowControl: 'none',
        bufferSize: 4096,
      })
      this.opened = true

      const signals = options.signals ?? {
        dataTerminalReady: true,
        requestToSend: true,
      }
      if (options.pulseSignals) {
        await this.setSignals({
          dataTerminalReady: false,
          requestToSend: false,
        })
        await new Promise((resolve) => setTimeout(resolve, 80))
      }
      await this.setSignals(signals)
      if (options.pulseSignals) {
        await new Promise((resolve) => setTimeout(resolve, 120))
      }

      if (!this.port.readable) {
        throw new Error('The selected serial port did not provide a readable stream.')
      }
      this.reader = this.port.readable.getReader()
      this.readLoop = this.pumpReads()
    } catch (error) {
      this.opened = false
      try {
        this.reader?.releaseLock()
      } catch {
        // The readable stream may already be locked by a failed open.
      }
      this.reader = null
      this.readLoop = null
      try {
        await this.port.close()
      } catch {
        // A failed open may leave the underlying device already closed.
      }
      throw error
    }
  }

  private async pumpReads(): Promise<void> {
    try {
      while (this.reader) {
        const { value, done } = await this.reader.read()
        if (done) break
        if (value?.byteLength) this.append(value)
      }
    } catch (cause) {
      if (this.opened) {
        this.failure = cause instanceof Error ? cause : new Error('Serial read failed.')
        this.notifyWaiters()
      }
    }
  }

  private append(chunk: Uint8Array): void {
    const next = new Uint8Array(this.buffer.byteLength + chunk.byteLength)
    next.set(this.buffer)
    next.set(chunk, this.buffer.byteLength)
    this.buffer = next
    this.notifyWaiters()
  }

  private notifyWaiters(): void {
    for (const waiter of this.waiters) waiter()
    this.waiters.clear()
  }

  private waitForData(timeoutMs: number): Promise<void> {
    return new Promise((resolve) => {
      let settled = false
      const finish = () => {
        if (settled) return
        settled = true
        clearTimeout(timer)
        this.waiters.delete(finish)
        resolve()
      }
      const timer = setTimeout(finish, timeoutMs)
      this.waiters.add(finish)
    })
  }

  async discardInput(): Promise<void> {
    this.buffer = new Uint8Array(0)
    await new Promise((resolve) => setTimeout(resolve, 20))
    this.buffer = new Uint8Array(0)
  }

  async write(data: Uint8Array): Promise<void> {
    if (!this.port.writable) throw new Error('The serial port is not writable.')
    const writer = this.port.writable.getWriter()
    try {
      await writer.write(data)
    } finally {
      writer.releaseLock()
    }
  }

  async setSignals(signals: SerialSignalState): Promise<void> {
    try {
      await this.port.setSignals?.(signals)
    } catch {
      // Some bridges do not permit changing modem-control lines.
    }
  }

  async reconfigure(options: {
    baudRate?: number
    stopBits?: 1 | 2
  }): Promise<void> {
    await this.close()
    if (options.baudRate !== undefined) this.baudRate = options.baudRate
    if (options.stopBits !== undefined) this.stopBits = options.stopBits
    await new Promise((resolve) => setTimeout(resolve, 50))
    await this.open()
  }

  async readExact(size: number, timeoutMs = 2500): Promise<Uint8Array> {
    const deadline = performance.now() + timeoutMs
    while (this.buffer.byteLength < size) {
      if (this.failure) throw this.failure
      const remaining = deadline - performance.now()
      if (remaining <= 0) {
        throw new Error(
          `Radio response timed out: expected ${size} bytes, received ${this.buffer.byteLength}.`,
        )
      }
      await this.waitForData(Math.min(remaining, READ_SLICE_MS))
    }

    const result = this.buffer.slice(0, size)
    this.buffer = this.buffer.slice(size)
    return result
  }

  async readUntil(delimiter: number, timeoutMs = 2500): Promise<Uint8Array> {
    const deadline = performance.now() + timeoutMs
    while (true) {
      const index = this.buffer.indexOf(delimiter)
      if (index >= 0) {
        const result = this.buffer.slice(0, index + 1)
        this.buffer = this.buffer.slice(index + 1)
        return result
      }
      if (this.failure) throw this.failure
      const remaining = deadline - performance.now()
      if (remaining <= 0) {
        throw new Error(
          `Radio response timed out before delimiter 0x${delimiter.toString(16)}; received ${this.buffer.byteLength} bytes.`,
        )
      }
      await this.waitForData(Math.min(remaining, READ_SLICE_MS))
    }
  }

  async close(): Promise<void> {
    this.opened = false
    if (this.reader) {
      try {
        await this.reader.cancel()
      } catch {
        // The cable may already have been removed.
      } finally {
        try {
          this.reader.releaseLock()
        } catch {
          // A failed stream may already have released its lock.
        }
      }
      this.reader = null
    }
    if (this.readLoop) await this.readLoop
    this.readLoop = null
    try {
      await this.port.close()
    } catch {
      // Closing an already-disconnected cable is harmless.
    }
    this.failure = null
    this.buffer = new Uint8Array(0)
    this.notifyWaiters()
  }

  async reopen(): Promise<void> {
    await this.close()
    await new Promise((resolve) => setTimeout(resolve, 350))
    await this.open()
  }
}
